import React from 'react';
import { CheckCircle, XCircle, Clock, Eye, Trash2, User, Briefcase, Building, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const roleColors = {
  ADMIN: 'bg-teal-600/10 text-teal-600 border-teal-600/20',
  CLIENT: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  TRANSPORTEUR: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
  AGENT: 'bg-amber-600/10 text-amber-700 border-amber-600/20',
};

const roleLabels = {
  ADMIN: 'Admin',
  CLIENT: 'Client',
  TRANSPORTEUR: 'Transporteur',
  AGENT: 'Agent',
};

// Helper pour extraire les infos du profil
const getProfileInfo = (user) => {
  const profile = user.profile_data;
  if (!profile) return null;

  switch (profile.type) {
    case 'client':
      return {
        type: 'Client',
        icon: User,
        details: profile.type_client === 'entreprise' 
          ? { label: profile.company_name || 'Entreprise', subLabel: 'Entreprise' }
          : { label: 'Particulier', subLabel: null },
        badge: profile.type_client,
      };
    case 'transporter':
      return {
        type: 'Transporteur',
        icon: Briefcase,
        details: {
          label: profile.organisation_name || 'Indépendant',
          subLabel: profile.verification_status,
        },
        badge: profile.verification_status,
        rating: profile.rating_avg,
      };
    case 'agent':
      return {
        type: 'Agent',
        icon: Building,
        details: {
          label: profile.role_level || '-',
          subLabel: null,
        },
        badge: profile.role_level,
      };
    default:
      return null;
  }
};

const PendingUsersTable = ({ users, onApprove, onReject, onViewDetails, onDelete, isLoading }) => {
  const [actionDialogOpen, setActionDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [actionType, setActionType] = React.useState(null); // 'approve', 'reject', 'delete'

  const handleActionClick = (user, type) => {
    setSelectedUser(user);
    setActionType(type);
    setActionDialogOpen(true);
  };

  const handleActionConfirm = () => {
    if (!selectedUser) return;

    switch (actionType) {
      case 'approve':
        onApprove(selectedUser.id);
        break;
      case 'reject':
        onReject(selectedUser.id);
        break;
      case 'delete':
        onDelete(selectedUser.id);
        break;
      default:
        break;
    }

    setActionDialogOpen(false);
    setSelectedUser(null);
    setActionType(null);
  };

  const getActionDialogContent = () => {
    switch (actionType) {
      case 'approve':
        return {
          title: 'Valider l\'utilisateur',
          description: `Êtes-vous sûr de vouloir valider l'utilisateur ${selectedUser?.email} ? Il pourra accéder à la plateforme.`,
          action: 'Valider',
          variant: 'default',
        };
      case 'reject':
        return {
          title: 'Rejeter l\'utilisateur',
          description: `Êtes-vous sûr de vouloir rejeter l'utilisateur ${selectedUser?.email} ? Son compte sera suspendu.`,
          action: 'Rejeter',
          variant: 'destructive',
        };
      case 'delete':
        return {
          title: 'Supprimer l\'utilisateur',
          description: `Êtes-vous sûr de vouloir supprimer définitivement l'utilisateur ${selectedUser?.email} ? Cette action est irréversible.`,
          action: 'Supprimer',
          variant: 'destructive',
        };
      default:
        return {
          title: '',
          description: '',
          action: '',
          variant: 'default',
        };
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-card rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-12 bg-card rounded-lg border border-border">
        <p className="text-muted-foreground">Aucun utilisateur en attente</p>
      </div>
    );
  }

  const dialogContent = getActionDialogContent();

  return (
    <>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Profil
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Inscription
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => {
                const profileInfo = getProfileInfo(user);
                const ProfileIcon = profileInfo?.icon || User;

                return (
                  <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                    {/* Utilisateur */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0 border border-yellow-500/30">
                          <Clock className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Rôle */}
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${roleColors[user.role]} font-medium`}>
                        {roleLabels[user.role] || user.role}
                      </Badge>
                    </td>

                    {/* Profil */}
                    <td className="px-4 py-3">
                      {profileInfo ? (
                        <div className="flex items-start gap-2">
                          <ProfileIcon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-foreground truncate max-w-[180px]">
                              {profileInfo.details.label}
                            </div>
                            {profileInfo.details.subLabel && (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {profileInfo.badge && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                    {profileInfo.badge}
                                  </Badge>
                                )}
                                {profileInfo.rating && parseFloat(profileInfo.rating) > 0 && (
                                  <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
                                    <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                                    <span className="font-medium">{parseFloat(profileInfo.rating).toFixed(1)}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>

                    {/* Contact */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-foreground">
                        {user.phone_number || '-'}
                      </div>
                    </td>

                    {/* Date d'inscription */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-muted-foreground">
                        {new Date(user.date_joined).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleActionClick(user, 'approve')}
                          className="h-8"
                        >
                          <CheckCircle className="h-4 w-4 mr-1.5" />
                          Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onViewDetails(user)}
                          className="h-8"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActionClick(user, 'reject')}
                          className="h-8 text-orange-500 hover:text-orange-600"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleActionClick(user, 'delete')}
                          className="h-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogContent.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleActionConfirm}
              className={dialogContent.variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              {dialogContent.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PendingUsersTable;

